import { useState, useEffect } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UserAccountSidebar, type UserAccountSection } from './user-account-sidebar'
import { useCurrentUser, useUpdateMe } from '@/stores/user.store'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { UpdateMeRequestSchema } from '@/gen/cortado/v1/users_pb'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { formatPhoneNumber, formatPhoneInput } from '@/lib/utils'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { useRichTextEditor } from '@/hooks/use-rich-text-editor'

interface UserAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function isRichTextHtmlEmpty(html: string) {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0
}

function normalizeSignatureHtml(html: string) {
  return isRichTextHtmlEmpty(html) ? '' : html
}

export function UserAccountDialog({ open, onOpenChange }: UserAccountDialogProps) {
  const [currentSection, setCurrentSection] = useState<UserAccountSection>('account')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl min-w-[1000px] h-[600px] p-0">
        <div className="flex h-full overflow-hidden">
          <div className="flex flex-col w-56 border-r">
            <DialogHeader className="px-6 py-4 border-b">
              <DialogTitle className="text-base">Account Settings</DialogTitle>
            </DialogHeader>
            <UserAccountSidebar
              currentSection={currentSection}
              onSectionChange={setCurrentSection}
            />
          </div>
          <div className="flex-1 overflow-auto p-6">
            {currentSection === 'account' && <AccountContent />}
            {currentSection === 'notifications' && <NotificationsContent />}
            {currentSection === 'settings' && <SettingsContent />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AccountContent() {
  const { user, isLoading, hasLoaded } = useCurrentUser()
  const { updateProfileAsync, isUpdating } = useUpdateMe()
  const [isEditing, setIsEditing] = useState(false)

  const form = useForm({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      emailSignatureHtml: user?.emailSignatureHtml ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        const request = create(UpdateMeRequestSchema, {
          firstName: value.firstName,
          lastName: value.lastName,
          phoneNumber: value.phoneNumber,
          emailSignatureHtml: normalizeSignatureHtml(value.emailSignatureHtml),
        })
        await updateProfileAsync(request)
        setIsEditing(false)
        toast.success('Profile updated')
      } catch (error) {
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'user-account-dialog' })
        toast.error('Failed to update profile', {
          description: getErrorDescription(error),
        })
      }
    },
  })
  const signatureEditor = useRichTextEditor({
    initialContent: user?.emailSignatureHtml ?? '',
    placeholder: 'Add your email signature...',
    editable: isEditing && !isUpdating,
    minHeight: '120px',
    onUpdate: (html) => {
      form.setFieldValue('emailSignatureHtml', normalizeSignatureHtml(html))
    },
  })

  useEffect(() => {
    if (user) {
      form.setFieldValue('firstName', user.firstName ?? '')
      form.setFieldValue('lastName', user.lastName ?? '')
      form.setFieldValue('phoneNumber', user.phoneNumber ?? '')
      form.setFieldValue('emailSignatureHtml', user.emailSignatureHtml ?? '')
      signatureEditor?.commands.setContent(user.emailSignatureHtml ?? '')
    }
  }, [user, isEditing, form, signatureEditor])

  useEffect(() => {
    signatureEditor?.setEditable(isEditing && !isUpdating)
  }, [signatureEditor, isEditing, isUpdating])

  const handleCancel = () => {
    if (user) {
      form.setFieldValue('firstName', user.firstName ?? '')
      form.setFieldValue('lastName', user.lastName ?? '')
      form.setFieldValue('phoneNumber', user.phoneNumber ?? '')
      form.setFieldValue('emailSignatureHtml', user.emailSignatureHtml ?? '')
      signatureEditor?.commands.setContent(user.emailSignatureHtml ?? '')
    }
    setIsEditing(false)
  }

  if (isLoading || !hasLoaded) {
    return <div>Loading...</div>
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight">Account</h2>
        <p className="text-muted-foreground">
          Manage your account information and preferences
        </p>
      </div>

      {isEditing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <div className="space-y-4">
            <form.Field name="firstName">
              {(field) => (
                <div className="space-y-2">
                  <label htmlFor="firstName" className="text-sm font-medium">
                    First Name
                  </label>
                  <Input
                    id="firstName"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isUpdating}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="lastName">
              {(field) => (
                <div className="space-y-2">
                  <label htmlFor="lastName" className="text-sm font-medium">
                    Last Name
                  </label>
                  <Input
                    id="lastName"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isUpdating}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="phoneNumber">
              {(field) => (
                <div className="space-y-2">
                  <label htmlFor="phoneNumber" className="text-sm font-medium">
                    Phone Number
                  </label>
                  <Input
                    id="phoneNumber"
                    value={formatPhoneInput(field.state.value)}
                    onChange={(e) => field.handleChange(e.target.value.replace(/\D/g, ''))}
                    disabled={isUpdating}
                    placeholder="(555) 555-5555"
                  />
                </div>
              )}
            </form.Field>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                value={user?.email ?? ''}
                disabled
              />
              <p className="text-sm text-muted-foreground">
                Email cannot be changed
              </p>
            </div>

            <form.Field name="emailSignatureHtml">
              {() => (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Email signature
                  </label>
                  <RichTextEditor
                    editor={signatureEditor}
                    disabled={isUpdating}
                    minHeight="120px"
                  />
                </div>
              )}
            </form.Field>
          </div>

          <div className="mt-6 flex gap-2">
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isUpdating}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">First Name</label>
              <Input value={user?.firstName ?? ''} disabled />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Last Name</label>
              <Input value={user?.lastName ?? ''} disabled />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone Number</label>
              <Input value={formatPhoneNumber(user?.phoneNumber)} disabled placeholder="Not set" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input value={user?.email ?? ''} disabled />
              <p className="text-sm text-muted-foreground">
                Email cannot be changed
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Email signature</label>
              <RichTextEditor
                editor={signatureEditor}
                disabled
                minHeight="120px"
              />
            </div>
          </div>
          <div className="mt-6">
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          </div>
        </>
      )}
    </>
  )
}

function NotificationsContent() {
  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight">Notifications</h2>
        <p className="text-muted-foreground">
          Configure how you receive notifications
        </p>
      </div>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          No additional notification channels are currently available.
        </p>
      </div>
    </>
  )
}

function SettingsContent() {
  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
        <p className="text-muted-foreground">
          General settings and preferences
        </p>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center">
        <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
          Coming soon
        </span>
        <p className="text-sm text-muted-foreground max-w-xs">
          Customize your timezone, date format, default calendar view, and other display preferences.
        </p>
      </div>
    </>
  )
}
